/**
 * Maps between the session payment-record shape (domain data only) and the
 * CostPaymentRecordParam view-model the costs screens render.
 *
 * All patient-facing copy — headlines, next actions, split lines and their
 * colors, date labels — is derived here at render time so session records stay
 * correct across locale switches, copy changes, and redesigns.
 */

import type {
  ApiPaymentRecord,
  UpdatePaymentRecordPayload,
} from "@src/packages/costs/hooks/usePaymentRecords";
import { formatCurrency, formatDate, type Locale } from "@src/packages/i18n";
import {
  getTodayString,
  parseDateOrTimestamp,
  toLocalDateString,
} from "@src/packages/utils/date";
import type {
  CostPaymentAllocationKind,
  CostPaymentLineItemParam,
  CostPaymentRecordParam,
  CostPaymentSplitLineParam,
} from "../../types";

// Same palette as PaymentRecordDetailModal's PAYMENT_SPLIT_COLORS.
const SPLIT_COLORS = {
  paid: "#F4B34F",
  claims: "#5B332C",
  left: "#B76545",
} as const;

// A record's `detail` is only populated from the user's own note. Previously
// manual entries got a long "This was added manually..." fallback and uploads
// got a "Waiting for the document read..." fallback — both read as noise in
// the detail modal, so we leave `detail` empty when the user hasn't written
// anything.
const MANUAL_FALLBACK_DETAIL = "";
const UPLOAD_WAITING_DETAIL = "";

export function todayIsoDate(): string {
  return getTodayString();
}

// Free-text date labels ("Today", "Added today") don't parse — callers fall
// back to keeping the stored date (updates) or today (creates).
export function parseDateText(text: string): string | undefined {
  const trimmed = text.trim();
  if (!trimmed) return undefined;
  // The row shows a formatted local day ("Oct 4, 2026"), which parses as
  // local midnight; toISOString would turn that into Oct 3 east of UTC.
  const parsed = parseDateOrTimestamp(trimmed);
  if (Number.isNaN(parsed.getTime())) return undefined;
  return toLocalDateString(parsed);
}

function getAllocationColor(kind: CostPaymentAllocationKind): string {
  if (kind === "insurance") return SPLIT_COLORS.claims;
  if (kind === "financing" || kind === "clinic") return SPLIT_COLORS.left;
  return SPLIT_COLORS.paid;
}

function getKindColor(kind: ApiPaymentRecord["kind"]): string {
  if (kind === "claim") return SPLIT_COLORS.claims;
  if (kind === "balance") return SPLIT_COLORS.left;
  return SPLIT_COLORS.paid;
}

function getKindSplitLabel(kind: ApiPaymentRecord["kind"]): string {
  if (kind === "claim") return "Claim amount";
  if (kind === "receipt") return "Receipt total";
  if (kind === "balance") return "Left to pay";
  return "Paid by you";
}

// Same aggregation the detail modal applies when saving edits.
function aggregateSplitLines(
  lineItems: CostPaymentLineItemParam[],
): CostPaymentSplitLineParam[] {
  const grouped = new Map<
    string,
    {
      id: string;
      label: string;
      amount: number;
      detailItems: string[];
      color: string;
    }
  >();

  lineItems.forEach((lineItem) => {
    lineItem.allocations.forEach((allocation) => {
      if (allocation.amount <= 0) return;
      const key = `${allocation.kind}:${allocation.label}`;
      const existing = grouped.get(key);

      if (existing) {
        existing.amount += allocation.amount;
        existing.detailItems.push(lineItem.label);
        return;
      }

      grouped.set(key, {
        id: `${allocation.kind}-${allocation.label
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")}`,
        label: allocation.label,
        amount: allocation.amount,
        detailItems: [lineItem.label],
        color: getAllocationColor(allocation.kind),
      });
    });
  });

  return Array.from(grouped.values()).map((line) => ({
    id: line.id,
    label: line.label,
    amount: line.amount,
    detail:
      line.detailItems.length === 1
        ? (line.detailItems[0] ?? "")
        : `${line.detailItems.length} items`,
    color: line.color,
  }));
}

function deriveSplitLines(
  record: ApiPaymentRecord,
): CostPaymentSplitLineParam[] {
  const lineItems = record.lineItems ?? [];
  if (lineItems.length > 0) return aggregateSplitLines(lineItems);

  const amount = record.amount ?? 0;
  if (record.entrySource === "manual" && amount > 0) {
    return [
      {
        id: `${record.id}-manual`,
        label: getKindSplitLabel(record.kind),
        amount,
        detail: "Entered manually",
        color: getKindColor(record.kind),
      },
    ];
  }
  return [];
}

function deriveDocuments(
  record: ApiPaymentRecord,
): CostPaymentRecordParam["documents"] {
  const stored = record.documents ?? [];
  if (stored.length === 0 && record.entrySource === "manual") {
    return [
      {
        id: `${record.id}-needed`,
        label: "Supporting document",
        detail: "Add a receipt, statement, claim, or EOB when available",
        status: "needed",
      },
    ];
  }

  return stored.map((line) => ({
    id: line.id,
    label: line.label,
    uploadId: line.uploadId ?? null,
    detail:
      line.status === "attached"
        ? "Attached to this ledger item"
        : line.status === "needed"
          ? "Add a receipt, statement, claim, or EOB when available"
          : "Pending",
    status: line.status,
  }));
}

export function apiToUiPaymentRecord(
  record: ApiPaymentRecord,
  locale: Locale,
): CostPaymentRecordParam {
  const amount = record.amount ?? 0;
  const isUpload = record.entrySource === "document_upload";
  const documents = deriveDocuments(record);
  const hasAttachedDocument = documents.some(
    (line) => line.status === "attached",
  );

  return {
    id: record.id,
    kind: record.kind,
    label: record.label ?? (isUpload ? "Uploaded payment document" : ""),
    source: isUpload
      ? (record.documents?.[0]?.label ?? "Uploaded document")
      : "Manual entry",
    provider: record.provider ?? "",
    phase: isUpload ? "Needs review" : "Manual",
    amount,
    billedAmount: record.billedAmount ?? 0,
    status: record.status ?? "review",
    occurredOn: record.occurredOn,
    date: record.occurredOn ? formatDate(record.occurredOn, locale) : "",
    headline: isUpload
      ? "Waiting for document read"
      : amount > 0
        ? `${formatCurrency(amount, locale)} recorded`
        : "Manual record added",
    note: record.note,
    detail: record.note?.trim()
      ? record.note
      : isUpload
        ? UPLOAD_WAITING_DETAIL
        : MANUAL_FALLBACK_DETAIL,
    coverage: isUpload
      ? "Pending match"
      : record.kind === "claim"
        ? "Match to EOB"
        : record.kind === "payment"
          ? "Save for reimbursement review"
          : "Confirm coverage path",
    financing: isUpload
      ? "Pending match"
      : record.kind === "balance"
        ? "Can plan now"
        : "Not financed yet",
    nextAction: isUpload
      ? "Review extracted lines"
      : hasAttachedDocument
        ? "Review document"
        : "Add document",
    splitLines: deriveSplitLines(record),
    lineItems: record.lineItems ?? undefined,
    documents,
    taxEligibility: record.taxEligibility ?? null,
  };
}

// Only domain fields enter the session cache. Derived copy stays local to the
// view model: the modal's free-text detail maps to note unless it's still the
// derived fallback, and an unparseable date edit keeps the stored date.
export function uiToPaymentRecordUpdates(
  record: CostPaymentRecordParam,
): UpdatePaymentRecordPayload {
  const detail = record.detail.trim();
  const note =
    record.note !== undefined
      ? record.note?.trim() || null
      : detail === MANUAL_FALLBACK_DETAIL || detail === UPLOAD_WAITING_DETAIL
        ? undefined
        : detail || undefined;
  const occurredOn = record.occurredOn ?? parseDateText(record.date);

  return {
    label: record.label,
    provider: record.provider,
    amount: record.amount,
    billedAmount: record.billedAmount,
    status: record.status,
    lineItems: record.lineItems ?? null,
    ...(note !== undefined ? { note } : {}),
    ...(occurredOn !== undefined ? { occurredOn } : {}),
    ...(record.taxEligibility ? { taxEligibility: record.taxEligibility } : {}),
  };
}
